import { useCallback, useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { mapi } from './api';
import { MasterLogin } from './MasterLogin';
import { CustomersView } from './CustomersView';
import { BillingView } from './BillingView';
import { Toast } from '../ui/Toast';
import { Empty } from '../ui/primitives';
import { VERSIO } from '../lib/version';

const Runko = styled.div`
  display: flex;
  align-items: flex-start;
  min-height: 100vh;
  @media (max-width: 800px) {
    flex-direction: column;
    align-items: stretch;
    width: 100%;
  }
`;

const Nav = styled.nav`
  width: 220px;
  flex: none;
  align-self: stretch;
  position: sticky;
  top: 0;
  height: 100vh;
  display: flex;
  flex-direction: column;
  padding: 18px 12px 14px;
  background: #2C211C;
  color: ${p => p.theme.color.onDark};
  button.valinta {
    display: flex;
    align-items: center;
    width: 100%;
    text-align: left;
    background: none;
    border: none;
    color: rgba(244, 244, 238, 0.86);
    font: inherit;
    font-weight: 500;
    padding: 9px 12px;
    border-radius: ${p => p.theme.radius.md};
    cursor: pointer;
    &:hover { background: rgba(244, 244, 238, 0.08); color: ${p => p.theme.color.onDark}; }
    &.active {
      color: ${p => p.theme.color.onDark};
      background: rgba(255, 127, 17, 0.28);
    }
  }
  @media (max-width: 800px) {
    width: 100%;
    height: auto;
    z-index: 30;
    padding: 0;
  }
`;

const Palkki = styled.div`
  display: contents;
  @media (max-width: 800px) {
    display: flex;
    align-items: center;
    gap: 4px;
    position: relative;
    z-index: 3;
    width: 100%;
    padding: 6px 8px 6px 4px;
  }
`;

const Brand = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 2px 12px 14px;
  font-size: 16px;
  font-weight: 650;
  letter-spacing: -0.03em;
  line-height: 1.25;
  .sub { font-weight: 500; color: rgba(244, 244, 238, 0.62); font-size: 13px; }
  @media (max-width: 800px) {
    padding: 4px 8px;
    flex: 1;
    font-size: 15px;
  }
`;

const Hampurilainen = styled.button<{ $auki: boolean }>`
  display: none;
  @media (max-width: 800px) {
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 5px;
    width: 44px;
    height: 44px;
    margin-left: auto;
    flex: none;
    padding: 12px 10px;
    border: none;
    border-radius: 8px;
    background: none;
    cursor: pointer;
    &:hover { background: rgba(244, 244, 238, 0.08); }
    span {
      display: block;
      height: 2px;
      border-radius: 2px;
      background: #F4F4EE;
      transition: transform 160ms ease, opacity 160ms ease;
    }
    ${p => p.$auki && `
      span:nth-child(1) { transform: translateY(7px) rotate(45deg); }
      span:nth-child(2) { opacity: 0; }
      span:nth-child(3) { transform: translateY(-7px) rotate(-45deg); }
    `}
  }
`;

const Tausta = styled.button`
  display: none;
  @media (max-width: 800px) {
    display: block;
    position: fixed;
    inset: 0;
    z-index: 1;
    border: none;
    padding: 0;
    background: rgba(28, 21, 18, 0.45);
    cursor: pointer;
  }
`;

const Valikko = styled.div<{ $auki: boolean }>`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;
  @media (max-width: 800px) {
    display: ${p => (p.$auki ? 'flex' : 'none')};
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    z-index: 2;
    flex: none;
    max-height: calc(100dvh - 56px);
    overflow: hidden;
    padding: 4px 12px 16px;
    background: #2C211C;
    box-shadow: 0 18px 40px rgba(28, 21, 18, 0.35);
  }
`;

const Valinnat = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-height: 0;
  overflow: auto;
`;

const Kayttaja = styled.div`
  margin-top: auto;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px 12px 4px;
  @media (max-width: 800px) { margin-top: 12px; }
`;

const Tili = styled.div<{ $auki: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column-reverse;
  .pudotus { display: none; }
  @media (hover: hover) and (pointer: fine) {
    &:hover .pudotus, &:focus-within .pudotus { display: flex; }
  }
  ${p => p.$auki && css`.pudotus { display: flex; }`}
`;

const TiliNappi = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  min-height: 52px;
  padding: 8px 4px;
  border: none;
  border-radius: ${p => p.theme.radius.md};
  background: none;
  color: ${p => p.theme.color.onDark};
  font: inherit;
  font-size: 16px;
  font-weight: 650;
  letter-spacing: -0.02em;
  text-align: left;
  cursor: pointer;
  &:hover { background: rgba(244, 244, 238, 0.08); }
  .nimi {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .nuoli {
    flex: none;
    width: 0;
    height: 0;
    border-left: 5px solid transparent;
    border-right: 5px solid transparent;
    border-bottom: 6px solid rgba(244, 244, 238, 0.8);
  }
`;

const Hahmo = styled.span`
  flex: none;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: grid;
  place-items: center;
  background: rgba(244, 244, 238, 0.14);
  color: #F4F4EE;
`;

const Pudotus = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 100%;
  z-index: 5;
  flex-direction: column;
  gap: 2px;
  padding: 6px;
  margin-bottom: 4px;
  background: #fff;
  color: #1A1A1A;
  border-radius: 12px;
  box-shadow: 0 10px 28px rgba(28, 21, 18, 0.28);
  button {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    padding: 9px 10px;
    border: none;
    border-radius: 8px;
    background: none;
    color: inherit;
    font: inherit;
    font-weight: 600;
    font-size: 14px;
    text-align: left;
    cursor: pointer;
    &:hover { background: #F3F3EF; }
  }
  @media (max-width: 800px) {
    position: static;
    margin: 0 0 6px;
  }
`;

const Main = styled.main`
  flex: 1;
  min-width: 0;
  padding: 24px 24px 0;
  @media (max-width: 800px) {
    width: 100%;
    max-width: 100%;
    padding: 16px 14px 0;
  }
`;

const Lataus = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
`;

type Tab = 'asiakkaat' | 'laskutus';

const VALILEHDET: [Tab, string][] = [
  ['asiakkaat', 'Asiakkaat'],
  ['laskutus', 'Laskutus'],
];

export function MasterApp() {
  const [tila, setTila] = useState<{ user: string | null; needSetup: boolean } | null>(null);
  const [tab, setTab] = useState<Tab>('asiakkaat');
  const [toast, setToast] = useState<string | null>(null);
  const [valikkoAuki, setValikkoAuki] = useState(false);
  const [kayttajaAuki, setKayttajaAuki] = useState(false);
  const tiliRef = useRef<HTMLDivElement>(null);

  const naytaToast = useCallback((v: string) => {
    setToast(v);
    window.setTimeout(() => setToast(nyt => (nyt === v ? null : nyt)), 2600);
  }, []);

  const tarkistaTila = useCallback(async () => {
    try {
      setTila(await mapi<{ user: string | null; needSetup: boolean }>('/api/master/authstate'));
    } catch {
      setTila({ user: null, needSetup: false });
    }
  }, []);

  useEffect(() => { void tarkistaTila(); }, [tarkistaTila]);

  useEffect(() => {
    if (!valikkoAuki) return;
    const sulje = (e: KeyboardEvent) => { if (e.key === 'Escape') setValikkoAuki(false); };
    window.addEventListener('keydown', sulje);
    return () => window.removeEventListener('keydown', sulje);
  }, [valikkoAuki]);

  const hiiriAvaa = () =>
    window.matchMedia('(hover: hover) and (pointer: fine)').matches;

  useEffect(() => {
    if (!kayttajaAuki) return;
    const sulje = (e: MouseEvent) => {
      if (tiliRef.current && !tiliRef.current.contains(e.target as Node)) setKayttajaAuki(false);
    };
    const nappain = (e: KeyboardEvent) => { if (e.key === 'Escape') setKayttajaAuki(false); };
    document.addEventListener('mousedown', sulje);
    document.addEventListener('keydown', nappain);
    return () => {
      document.removeEventListener('mousedown', sulje);
      document.removeEventListener('keydown', nappain);
    };
  }, [kayttajaAuki]);

  if (!tila) return <Lataus><Empty>Ladataan…</Empty></Lataus>;
  if (!tila.user) return <MasterLogin needSetup={tila.needSetup} onDone={tarkistaTila} />;

  const logout = async () => {
    await mapi('/api/master/logout', 'POST', {});
    setKayttajaAuki(false);
    await tarkistaTila();
  };

  return (
    <>
      <Runko>
        <Nav>
          <Palkki>
            <Brand>
              <span>ISOFAN</span>
              <span className="sub">Master</span>
            </Brand>
            <Hampurilainen
              type="button"
              $auki={valikkoAuki}
              aria-expanded={valikkoAuki}
              aria-controls="mastervalikko"
              aria-label={valikkoAuki ? 'Sulje valikko' : 'Avaa valikko'}
              onClick={() => setValikkoAuki(a => !a)}
            >
              <span /><span /><span />
            </Hampurilainen>
          </Palkki>
          {valikkoAuki && (
            <Tausta type="button" aria-label="Sulje valikko" onClick={() => setValikkoAuki(false)} />
          )}
          <Valikko id="mastervalikko" $auki={valikkoAuki}>
            <Valinnat>
              {VALILEHDET.map(([id, label]) => (
                <button
                  key={id}
                  type="button"
                  className={`valinta${tab === id ? ' active' : ''}`}
                  aria-current={tab === id ? 'page' : undefined}
                  onClick={() => { setTab(id); setValikkoAuki(false); }}
                >
                  {label}
                </button>
              ))}
            </Valinnat>
            <Kayttaja>
              <Tili ref={tiliRef} $auki={kayttajaAuki}>
                <TiliNappi
                  type="button"
                  aria-haspopup="menu"
                  aria-expanded={kayttajaAuki}
                  onClick={() => { if (!hiiriAvaa()) setKayttajaAuki(a => !a); }}
                >
                  <Hahmo aria-hidden="true">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                      <circle cx="12" cy="8" r="4" />
                      <path d="M5.2 19.4c.7-3.1 3.4-5 6.8-5s6.1 1.9 6.8 5c.1.5-.3.8-.8.8H6c-.5 0-.9-.3-.8-.8z" />
                    </svg>
                  </Hahmo>
                  <span className="nimi">{tila.user}</span>
                  <span className="nuoli" />
                </TiliNappi>
                <Pudotus className="pudotus" role="menu">
                  <button type="button" role="menuitem" onClick={logout}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                      <path d="M9 6H6a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h3" />
                      <path d="M13 16l4-4-4-4" />
                      <path d="M9 12h8" />
                    </svg>
                    Kirjaudu ulos
                  </button>
                </Pudotus>
              </Tili>
            </Kayttaja>
          </Valikko>
        </Nav>
        <Main>
          <h2>{tab === 'asiakkaat' ? 'Asiakkaat' : 'Laskutus'}</h2>
          {tab === 'asiakkaat' ? <CustomersView onToast={naytaToast} /> : <BillingView />}
          <div style={{ textAlign: 'right', fontSize: 12, color: '#5E5E58', margin: '26px 0 4px' }}>
            ISOFAN Master v{VERSIO}
          </div>
        </Main>
      </Runko>
      <Toast viesti={toast} />
    </>
  );
}
